/**
 * Canonical coded option lists for provider profile fields (specialties, modalities, etc.).
 *
 * Single source of truth for both medplum-ui and medplum-server — import via
 * `@unify/fhir-model/provider-profile-datasets`. Do not copy this file into
 * either app.
 *
 * Use `code` for validation/storage; `display` for UI labels.
 */

export interface CodedOption {
  code: string;
  display: string;
}

export const PROVIDER_SPECIALTIES: readonly CodedOption[] = [
  { code: 'addiction', display: 'Addiction' },
  { code: 'adhd', display: 'ADHD' },
  { code: 'anger-management', display: 'Anger Management' },
  { code: 'anxiety', display: 'Anxiety' },
  { code: 'autism', display: 'Autism' },
  { code: 'behavioral-issues', display: 'Behavioral Issues' },
  { code: 'bipolar-disorder', display: 'Bipolar Disorder' },
  { code: 'body-image-eating-disorders', display: 'Body Image & Eating Disorders' },
  { code: 'borderline-personality-bpd', display: 'Borderline Personality (BPD)' },
  { code: 'chronic-illness-pain', display: 'Chronic Illness & Pain' },
  { code: 'codependency', display: 'Codependency' },
  { code: 'dementia', display: 'Dementia' },
  { code: 'depression', display: 'Depression' },
  { code: 'dissociative-disorders-did', display: 'Dissociative Disorders (DID)' },
  { code: 'divorce', display: 'Divorce' },
  { code: 'domestic-abuse', display: 'Domestic Abuse' },
  { code: 'dual-diagnosis', display: 'Dual Diagnosis' },
  { code: 'first-responders-veterans', display: 'First Responders & Veterans' },
  { code: 'gambling-behavioral-addictions', display: 'Gambling & Behavioral Addictions' },
  { code: 'geriatric', display: 'Geriatric' },
  { code: 'grief', display: 'Grief' },
  { code: 'hoarding', display: 'Hoarding' },
  { code: 'infertility', display: 'Infertility' },
  { code: 'infidelity', display: 'Infidelity' },
  { code: 'marriage-issues', display: 'Marriage Issues' },
  { code: 'relationship-issues', display: 'Relationship Issues' },
  { code: 'medication-management', display: 'Medication Management' },
  { code: 'mens-issues', display: "Men's Issues" },
  { code: 'narcissistic-personality-npd', display: 'Narcissistic Personality (NPD)' },
  { code: 'obsessive-compulsive-ocd', display: 'Obsessive-Compulsive (OCD)' },
  { code: 'oppositional-defiance-odd', display: 'Oppositional Defiance (ODD)' },
  { code: 'parenting', display: 'Parenting' },
  { code: 'adoption', display: 'Adoption' },
  { code: 'pregnancy-prenatal-postpartum', display: 'Pregnancy, Prenatal, Postpartum' },
  { code: 'psychosis', display: 'Psychosis' },
  { code: 'racial-identity', display: 'Racial Identity' },
  { code: 'self-esteem', display: 'Self Esteem' },
  { code: 'self-harm-suicidal-ideation', display: 'Self-Harm & Suicidal Ideation' },
  { code: 'sex-therapy-sexual-addiction', display: 'Sex Therapy & Sexual Addiction' },
  { code: 'sexual-abuse', display: 'Sexual Abuse' },
  { code: 'sleep-insomnia', display: 'Sleep & Insomnia' },
  { code: 'social-anxiety', display: 'Social Anxiety' },
  { code: 'spirituality', display: 'Spirituality' },
  { code: 'sports-performance', display: 'Sports Performance' },
  { code: 'stress', display: 'Stress' },
  { code: 'substance-use', display: 'Substance Use' },
  { code: 'testing-evaluation', display: 'Testing & Evaluation' },
  { code: 'trauma-ptsd', display: 'Trauma & PTSD' },
  { code: 'womens-issues', display: "Women's Issues" },
] as const;

/** Gender identity (Male / Female / Non-binary only). */
export const GENDER_OPTIONS: readonly CodedOption[] = [
  { code: 'male', display: 'Male' },
  { code: 'female', display: 'Female' },
  { code: 'non-binary', display: 'Non-binary' },
] as const;

/** Sexual orientation / LGBTQ+ identity options (separate from gender identity). */
export const SEXUALITY_OPTIONS: readonly CodedOption[] = [
  { code: 'bisexual', display: 'Bisexual' },
  { code: 'gay', display: 'Gay' },
  { code: 'lesbian', display: 'Lesbian' },
  { code: 'lgbtq-plus', display: 'LGBTQ+' },
  { code: 'polyamory-enm', display: 'Polyamory & ENM' },
  { code: 'sex-positive-kink-friendly', display: 'Sex-Positive & Kink-Friendly' },
  { code: 'transgender', display: 'Transgender' },
] as const;

export const THERAPY_TYPES: readonly CodedOption[] = [
  { code: 'act', display: 'Acceptance and Commitment (ACT)' },
  { code: 'adlerian', display: 'Adlerian' },
  { code: 'aedp', display: 'AEDP' },
  { code: 'applied-behavioral-analysis-aba', display: 'Applied Behavioral Analysis (ABA)' },
  { code: 'art-therapy', display: 'Art Therapy' },
  { code: 'attachment-based', display: 'Attachment-based' },
  { code: 'biofeedback', display: 'Biofeedback' },
  { code: 'brainspotting', display: 'Brainspotting' },
  {
    code: 'clinical-supervision-licensed-supervisors',
    display: 'Clinical Supervision and Licensed Supervisors',
  },
  { code: 'coaching', display: 'Coaching' },
  { code: 'cognitive-behavioral-cbt', display: 'Cognitive Behavioral (CBT)' },
  { code: 'cognitive-processing-cpt', display: 'Cognitive Processing (CPT)' },
  { code: 'compassion-focused', display: 'Compassion Focused' },
  { code: 'couples-counseling', display: 'Couples Counseling' },
  { code: 'culturally-sensitive', display: 'Culturally Sensitive' },
  { code: 'dance-movement-therapy', display: 'Dance Movement Therapy' },
  { code: 'dialectical-behavior-dbt', display: 'Dialectical Behavior (DBT)' },
  { code: 'eclectic', display: 'Eclectic' },
  { code: 'emdr', display: 'EMDR' },
  { code: 'eft', display: 'EFT' },
  { code: 'energy-psychology', display: 'Energy Psychology' },
  { code: 'existential', display: 'Existential' },
  { code: 'experiential-therapy', display: 'Experiential Therapy' },
  { code: 'exposure-response-prevention-erp', display: 'Exposure Response Prevention (ERP)' },
  { code: 'expressive-arts', display: 'Expressive Arts' },
  { code: 'family-systems', display: 'Family Systems' },
  { code: 'family-therapy', display: 'Family Therapy' },
  { code: 'feminist', display: 'Feminist' },
  { code: 'forensic-psychology', display: 'Forensic Psychology' },
  { code: 'gestalt', display: 'Gestalt' },
  { code: 'gottman-method', display: 'Gottman Method' },
  { code: 'humanistic', display: 'Humanistic' },
  { code: 'hypnotherapy', display: 'Hypnotherapy' },
  { code: 'imago', display: 'Imago' },
  { code: 'integrative', display: 'Integrative' },
  { code: 'internal-family-systems-ifs', display: 'Internal Family Systems (IFS)' },
  { code: 'interpersonal', display: 'Interpersonal' },
  { code: 'jungian', display: 'Jungian' },
  { code: 'ketamine-assisted', display: 'Ketamine-Assisted' },
  { code: 'mindfulness-based-mbct', display: 'Mindfulness-Based (MBCT)' },
  { code: 'motivational-interviewing', display: 'Motivational Interviewing' },
  { code: 'multicultural', display: 'Multicultural' },
  { code: 'music-therapy', display: 'Music Therapy' },
  { code: 'narrative', display: 'Narrative' },
  { code: 'neurofeedback', display: 'Neurofeedback' },
  { code: 'neuro-linguistic-nlp', display: 'Neuro-Linguistic (NLP)' },
  { code: 'parent-child-interaction-pcit', display: 'Parent-Child Interaction (PCIT)' },
  { code: 'person-centered', display: 'Person-Centered' },
  { code: 'play-therapy', display: 'Play Therapy' },
  { code: 'positive-psychology', display: 'Positive Psychology' },
  { code: 'prolonged-exposure-therapy', display: 'Prolonged Exposure Therapy' },
  { code: 'psychoanalytic', display: 'Psychoanalytic' },
  {
    code: 'psychobiological-approach-couple-therapy',
    display: 'Psychobiological Approach Couple Therapy',
  },
  { code: 'psychodynamic', display: 'Psychodynamic' },
  { code: 'rational-emotive-behavior-rebt', display: 'Rational Emotive Behavior (REBT)' },
  { code: 'reality-therapy', display: 'Reality Therapy' },
  { code: 'relational', display: 'Relational' },
  { code: 'sandplay', display: 'Sandplay' },
  { code: 'schema-therapy', display: 'Schema Therapy' },
  { code: 'solution-focused-brief-sfbt', display: 'Solution Focused Brief (SFBT)' },
  { code: 'somatic', display: 'Somatic' },
  { code: 'strength-based', display: 'Strength-Based' },
  { code: 'structural-family-therapy', display: 'Structural Family Therapy' },
  { code: 'transpersonal', display: 'Transpersonal' },
  { code: 'trauma-focused', display: 'Trauma Focused' },
] as const;

export const ETHNICITY_OPTIONS: readonly CodedOption[] = [
  { code: 'arab-middle-eastern', display: 'Arab and Middle Eastern' },
  { code: 'asian', display: 'Asian' },
  { code: 'black-african-american', display: 'Black and African American' },
  { code: 'hispanic-latino', display: 'Hispanic and Latino' },
  { code: 'multiracial', display: 'Multiracial' },
  { code: 'native-american', display: 'Native American' },
  { code: 'pacific-islander', display: 'Pacific Islander' },
  { code: 'south-asian', display: 'South Asian' },
] as const;

export const FAITH_OPTIONS: readonly CodedOption[] = [
  { code: 'buddhist', display: 'Buddhist' },
  { code: 'christian', display: 'Christian' },
  { code: 'hindu', display: 'Hindu' },
  { code: 'jewish', display: 'Jewish' },
  { code: 'muslim', display: 'Muslim' },
  { code: 'secular-non-religious', display: 'Secular and Non-Religious' },
  { code: 'sikh', display: 'Sikh' },
  {
    code: 'church-jesus-christ-latter-day-saints',
    display: 'The Church of Jesus Christ of Latter-day Saints',
  },
] as const;

/** Languages spoken — curated list matching provider onboarding and client intake. */
export const LANGUAGE_OPTIONS: readonly CodedOption[] = [
  { code: 'ar', display: 'Arabic' },
  { code: 'hy', display: 'Armenian' },
  { code: 'yue', display: 'Cantonese' },
  { code: 'en', display: 'English' },
  { code: 'fa', display: 'Farsi' },
  { code: 'fr', display: 'French' },
  { code: 'de', display: 'German' },
  { code: 'he', display: 'Hebrew' },
  { code: 'hi', display: 'Hindi' },
  { code: 'ko', display: 'Korean' },
  { code: 'zh', display: 'Mandarin' },
  { code: 'ru', display: 'Russian' },
  { code: 'es', display: 'Spanish' },
  { code: 'tl', display: 'Tagalog (incl. Filipino)' },
  { code: 'vi', display: 'Vietnamese' },
] as const;

/** Display strings only (for legacy string[] form fields). */
export function codedOptionDisplays(options: readonly CodedOption[]): string[] {
  return options.map((o) => o.display);
}

/** Valid codes for a dataset (for runtime validation). */
export function codedOptionCodes(options: readonly CodedOption[]): Set<string> {
  return new Set(options.map((o) => o.code));
}
